import {useCallback, useEffect, useRef} from 'react'
import FilterDropdown, {
  FILTER_SELECT_ALL,
  SelectAllDropdownItem,
} from './FilterDropdown.js'
import {simpleFilter} from '../tokenizeOptions.js'
import {
  getCartName,
  cartOptionsSelector,
  cartOptionTokensSelector,
} from '../../data/carts.js'
import {updateForm, useAutoForm, useSelector} from '../../store.js'
import {useZropdown, useZropdownElement} from './Zropdown.js'
import {currentDropdownSelector} from '../../redux/selectors/ui/index.js'
import DropdownItem from './Dropdown/DropdownItem.js'
import Icon from '../icons/Icon.js'
import className from '../className.js'
import Checkbox from './Checkbox.js'
import {showEditCartModal} from '../../ordoro/Settings/Modals/EditCartModal.js'

function CartDropdownItem({option, isFocused, onSelect}) {
  const cart = option.entity
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <DropdownItem>
      <div
        className={`wrap--dropdown-item flex--justify-nowrap label--selectable label--selectable-pt-pb-0 margin-bottom-0 ${
          isFocused ? 'list__link--dropdown-arrow-focus' : ''
        }`}
      >
        <button
          className="btn--link list__link--dropdown list__link--cart"
          type="button"
          onClick={(event) => onSelect(option, event)}
        >
          <span className="btn__text">{getCartName(cart)}</span>
        </button>
        <span className="wrap--additional-buttons">
          <button
            className="btn btn--link no-underline margin-right-3"
            type="button"
            title="Edit Preset"
            onClick={() => showEditCartModal({cartID: cart.id})}
            data-dropdown-prevent-close={false}
          >
            <Icon name="pencil" className="fs-00" />
          </button>
        </span>
      </div>
    </DropdownItem>
  )
}

function CartCheckboxItem({option, selected, disabled, isFocused, onSelect}) {
  const cart = option.entity
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li
      ref={ref}
      className="list__item list__item--dropdown wrap--dropdown-item wrap--dropdown-item-np"
    >
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap label--selectable-left-indent margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
          disabled,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow mw-unset margin-bottom-0"
          htmlFor={cart.id}
          data-dropdown-prevent-close
        >
          <span className="flex margin-right-10">
            <span className="margin-right-5">
              <Checkbox
                mode="fancy"
                className="fs-00 v-align-middle"
                id={cart.id}
                checked={selected}
                onClick={(value, event) => {
                  if (!disabled) {
                    onSelect(option, event)
                  }
                }}
              />
            </span>
            <span className="label--selectable__text">{option.display}</span>
          </span>
          <span className="wrap--additional-buttons">
            <button
              className="btn btn--link no-underline margin-right-3"
              type="button"
              title="Edit Preset"
              onClick={() => showEditCartModal({cartID: cart.id})}
              data-dropdown-prevent-close={false}
            >
              <Icon name="pencil" className="fs-00" />
            </button>
          </span>
        </label>
      </span>
    </li>
  )
}

export default function CartFilter({
  dropdown = 'cart_filter_dropdown',
  label = 'Select Sales Channel',
  value,
  hasErrors,
  onSelect,
  onChange,
  excludeCartIDs,
  includeSelectAll = false,
}) {
  const isMultiSelect = Array.isArray(value)

  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    hasChanged: false,
    serverError: null,
  })

  const cartOptions = useSelector(cartOptionsSelector)
  const cartOptionTokens = useSelector(cartOptionTokensSelector)

  const onFilter = useCallback(() => {
    simpleFilter({
      formName: dropdown,
      options: cartOptions,
      optionTokens: cartOptionTokens,
      excludeIDs: excludeCartIDs,
      includeSelectAll,
    })
  }, [cartOptions, excludeCartIDs])

  useEffect(onFilter, [cartOptions, excludeCartIDs])

  const buttonRef = useRef()

  const localOnChange = useCallback(() => {
    updateForm(dropdown, {hasChanged: true})
  }, [])

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: onFilter,
      onSelect: (...args) => {
        onSelect(...args)

        localOnChange()
      },
    })

  const currentDropdown = useSelector(currentDropdownSelector)

  useEffect(() => {
    if (!currentDropdown && form.hasChanged) {
      updateForm(dropdown, {hasChanged: false})

      if (onChange) {
        onChange()
      }
    }
  }, [currentDropdown])

  return (
    <div
      className={className`wrap-outer--tag-dropdown-with-filter margin-right-20 ${{
        open: currentDropdown === dropdown,
      }}`}
      data-dropdown={dropdown}
    >
      <button
        ref={buttonRef}
        className="btn btn--dropdown btn--filter"
        type="button"
      >
        <strong
          className={className`btn__text ${{'error-message': hasErrors}}`}
        >
          {label}
        </strong>
      </button>
      <FilterDropdown
        dropdown={dropdown}
        form={form}
        onChangeInternal={onChangeInternal}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
        placeholder="Search Sales Channels"
        includeSearchInput
        parentRef={buttonRef}
      >
        <>
          {form.results.map((option, index) =>
            option.type === FILTER_SELECT_ALL ? (
              <SelectAllDropdownItem
                key={FILTER_SELECT_ALL}
                option={option}
                selectedList={value}
                isFocused={index === form.selectedIndex}
                onSelect={onSelectInternal}
              />
            ) : isMultiSelect ? (
              <CartCheckboxItem
                key={option.entity.id}
                option={option}
                onSelect={onSelectInternal}
                selected={value.includes(option.entity.id)}
                isFocused={index === form.selectedIndex}
              />
            ) : (
              <CartDropdownItem
                key={option.entity.id}
                option={option}
                onSelect={onSelectInternal}
                selected={value.includes(option.entity.id)}
                isFocused={index === form.selectedIndex}
              />
            ),
          )}
          {form.hasFoundNothing && (
            <li className="list__item--dropdown list__item--dropdown-empty align-center">
              No sales channels match your search.
            </li>
          )}
        </>
      </FilterDropdown>
    </div>
  )
}
